/**
 * Quantity Pack Selector
 * Sets form quantity based on selected pack tier.
 * Reads pricing data from data attributes set by Liquid.
 */

class QuantityPackSelector {
  constructor(element) {
    if (element.dataset.initialized === 'true') return;
    element.dataset.initialized = 'true';

    this.element = element;
    this.radioInputs = this.element.querySelectorAll('.qty-pack-selector__radio');
    this.cards = this.element.querySelectorAll('.qty-pack-row');
    this.productForm = this.element.closest('product-form') || this.element.closest('form');
    this.quantityInput = this.element.querySelector('.qty-pack-selector__quantity-input');
    this.variantInputs = this.productForm?.querySelectorAll('input[name="id"]') || [];
    this.addToCartBtn = this.productForm?.querySelector('.js-add-to-cart');
    this.sellingPlanInput = this.element.querySelector('input[name="selling_plan"]');
    this.addToCartInitiallyDisabled = Boolean(this.addToCartBtn?.disabled);

    this.init();
  }

  init() {
    this.radioInputs.forEach((radio) => {
      radio.addEventListener('change', this.handleTierChange.bind(this));
    });

    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.classList.contains('qty-pack-selector__radio')) return;
        const radio = card.querySelector('.qty-pack-selector__radio');
        if (radio && !radio.disabled) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    // Initialize with currently selected tier
    const selectedRadio = this.element.querySelector('.qty-pack-selector__radio:checked');
    if (selectedRadio) {
      this.updateSelection(selectedRadio);
      this.updateFormVariant(selectedRadio.closest('.qty-pack-row'));
      this.updateButtonPrice(selectedRadio.closest('.qty-pack-row'));
      this.updateGiftEligibility(selectedRadio.closest('.qty-pack-row'));
      this.updateSellingPlan(selectedRadio.closest('.qty-pack-row'));
    }
  }

  handleTierChange(event) {
    const radio = event.target;
    const quantity = radio.value;
    if (!quantity) return;

    this.updateSelection(radio);
    this.updateFormQuantity(quantity);
    this.updateFormVariant(radio.closest('.qty-pack-row'));
    this.updateButtonPrice(radio.closest('.qty-pack-row'));
    this.updateGiftEligibility(radio.closest('.qty-pack-row'));
    this.updateSellingPlan(radio.closest('.qty-pack-row'));
  }

  updateSelection(selectedRadio) {
    this.cards.forEach((card) => {
      card.classList.remove('qty-pack-row--selected');
    });
    const selectedCard = selectedRadio.closest('.qty-pack-row');
    if (selectedCard) {
      selectedCard.classList.add('qty-pack-row--selected');
    }
  }

  updateFormQuantity(quantity) {
    // Update the hidden quantity input inside the pack selector
    if (this.quantityInput) {
      this.quantityInput.value = quantity;
    }

    // Also update any other quantity input in the product form
    const formQuantityInput = this.productForm?.querySelector('input[name="quantity"]:not(.qty-pack-selector__quantity-input)');
    if (formQuantityInput) {
      formQuantityInput.value = quantity;
    }
  }

  updateFormVariant(card) {
    if (!card || !this.variantInputs.length || !card.dataset.tierVariantId) return;

    this.variantInputs.forEach((input) => {
      input.value = card.dataset.tierVariantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateButtonPrice(card) {
    if (!this.addToCartBtn || !card) return;

    const totalFormatted = card.dataset.tierTotalPriceFormatted;
    if (!totalFormatted) return;

    // Find or create the price span inside the button
    let priceSpan = this.addToCartBtn.querySelector('.qty-pack-atc-price');
    if (!priceSpan) {
      priceSpan = document.createElement('span');
      priceSpan.className = 'qty-pack-atc-price';
      const textSpan = this.addToCartBtn.querySelector('span:not(.qty-pack-atc-price)');
      if (textSpan) {
        textSpan.after(document.createTextNode(' '), priceSpan);
      } else {
        this.addToCartBtn.appendChild(priceSpan);
      }
    }

    const compareFormatted = card.dataset.tierCompareTotalFormatted;
    priceSpan.replaceChildren(document.createTextNode(totalFormatted));
    if (compareFormatted) {
      const compare = document.createElement('s');
      compare.textContent = compareFormatted;
      priceSpan.append(document.createTextNode(' '), compare);
    }

  }

  updateSellingPlan(card) {
    if (!card) return;

    // Subscription rows carry data-tier-selling-plan; one-time rows do not.
    if (this.sellingPlanInput) {
      const planId = card.dataset.tierSellingPlan || '';
      this.sellingPlanInput.value = planId;
      this.sellingPlanInput.disabled = !planId;
    }

    // A row whose selling plan is not configured yet must not be purchasable
    // at the preview price. Never re-enable a button disabled by Liquid
    // (sold out / no variant selected).
    if (this.addToCartBtn && !this.addToCartInitiallyDisabled) {
      this.addToCartBtn.disabled = card.dataset.tierPlanMissing === 'true';
    }
  }

  updateGiftEligibility(card) {
    if (!card) return;

    // Extra first-order gift (quarterly subscription row only).
    const extraGiftProperty = this.element.querySelector('.vsub-extra-gift-property');
    if (extraGiftProperty) {
      extraGiftProperty.disabled = card.dataset.tierExtraGift !== 'true';
    }

    const giftProperty = this.element.querySelector('.volume-tier-selector__gift-property');
    if (!giftProperty) return;

    // Rows that declare eligibility explicitly (subscription rows) win over the quantity rule.
    if (card.dataset.tierGiftEligible !== undefined) {
      giftProperty.disabled = card.dataset.tierGiftEligible !== 'true';
      return;
    }

    const quantity = Number(
      card.dataset.tierQty ||
      card.querySelector('.qty-pack-selector__radio')?.value ||
      0
    );

    // The Pastillero marker is submitted for the 2- and 3-unit volume offers.
    giftProperty.disabled = quantity < 2;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-pack-selector]').forEach((el) => {
    if (el.dataset.initialized !== 'true') new QuantityPackSelector(el);
  });
});

if (window.MutationObserver) {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('[data-pack-selector]')) {
          if (node.dataset.initialized !== 'true') new QuantityPackSelector(node);
        }
        node.querySelectorAll?.('[data-pack-selector]').forEach((el) => {
          if (el.dataset.initialized !== 'true') new QuantityPackSelector(el);
        });
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
